import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  FiSearch, FiDownload, FiPrinter, FiBarChart2, FiDollarSign, FiCreditCard, FiArrowUp, FiArrowDown
} from 'react-icons/fi';
import { colors } from '../../theme/colors';
import { formatCurrency, formatNumber, formatDate as fmtDate } from '../../utils/formatters';
import { collection, getDocs, query, where, orderBy, getAggregateFromServer, sum } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { recordReads } from '../../utils/readTelemetry';
import { useAuth } from '../../context/AuthContext';
import PageLayout from '../../components/PageLayout';
import { addPrintBrand } from '../../utils/printBranding';

// Cash accounts sit under 11101, Bank accounts under 11102 (default COA).
const CASH_PREFIX = '11101';
const BANK_PREFIX = '11102';
const acctKind = (code) => (String(code || '').startsWith(CASH_PREFIX) ? 'Cash'
  : String(code || '').startsWith(BANK_PREFIX) ? 'Bank' : '');

const formatAmount = (amount) => {
  const dp = (typeof window !== 'undefined' && Number.isFinite(Number(window.__AMAL_DECIMAL_PLACES__)))
    ? Number(window.__AMAL_DECIMAL_PLACES__) : 2;
  if (!amount && amount !== 0) return dp > 0 ? '0.' + '0'.repeat(dp) : '0';
  return Number(amount).toLocaleString('en-PK', { minimumFractionDigits: dp, maximumFractionDigits: dp });
};

const formatDate = (v) => fmtDate(v) || '-';

const CashBankBalances = () => {
  const { activeCompany, activeFinancialYear, activeBranch } = useAuth();
  const navigate = useNavigate();

  const [accounts, setAccounts] = useState([]);
  // { accountId: { opening, debit, credit } } — computed server-side, not from
  // a downloaded ledger.
  const [balances, setBalances] = useState({});
  const [financialYears, setFinancialYears] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);

  const [selectedFYId, setSelectedFYId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);
  const [kindFilter, setKindFilter] = useState('all'); // all | Cash | Bank
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState('code');
  const [sortDir, setSortDir] = useState('asc');

  const loadFinancialYears = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const snapshot = await getDocs(query(collection(db, 'companies', activeCompany.id, 'financialYears'), orderBy('startDate', 'desc')));
      recordReads(activeCompany.id, snapshot.size);
      const fys = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setFinancialYears(fys);
      if (activeFinancialYear?.id) {
        setSelectedFYId(activeFinancialYear.id);
        const activeFY = fys.find(f => f.id === activeFinancialYear.id);
        if (activeFY) setDateFrom(activeFY.startDate || '');
      } else if (fys.length > 0) {
        setSelectedFYId(fys[0].id);
        setDateFrom(fys[0].startDate || '');
      }
    } catch (error) {
      console.error('Error loading financial years:', error);
    }
  }, [activeCompany, activeFinancialYear]);

  // Load only Cash (11101) & Bank (11102) postable accounts.
  // COST: scoped server-side by code range — this used to download the whole
  // chart of accounts (~600 docs) to keep the ~20 that are cash/bank.
  const loadAccounts = useCallback(async () => {
    if (!activeCompany?.id) return [];
    try {
      const snapshot = await getDocs(query(
        collection(db, 'companies', activeCompany.id, 'accounts'),
        where('code', '>=', CASH_PREFIX),
        where('code', '<', '11103')          // covers 11101* (cash) + 11102* (bank)
      ));
      recordReads(activeCompany.id, snapshot.size);
      return snapshot.docs.map(d => ({ id: d.id, ...d.data() }))
        .filter(a => (a.level === 5 || a.isPostable) && !!acctKind(a.code));
    } catch (error) {
      console.error('Error loading accounts:', error);
      return [];
    }
  }, [activeCompany]);

  /**
   * Per-account opening / debit / credit via SERVER-SIDE SUMS.
   *
   * COST: this replaces downloading the entire financial year's ledger
   * (~83,000 documents) on every Generate. Firestore adds the numbers up and
   * returns totals, billed at roughly one read per 1,000 index entries — so
   * ~20 accounts cost tens of reads instead of tens of thousands. The figures
   * stay exact and live; nothing is stored, so nothing can drift.
   *
   * The filter is reproduced EXACTLY as the old client-side code had it:
   *   count a row when status === 'Posted'
   *      OR (status === 'Draft' AND tranTypeCode === 'OB')
   *   scoped to the selected FY, and to the active branch when one is chosen.
   *
   * The Draft+OB half is fetched as documents rather than aggregated: opening
   * balances are a handful of rows (one voucher), and a second aggregation per
   * account per window would double the round trips for almost no data.
   */
  const loadBalances = useCallback(async (accs) => {
    if (!activeCompany?.id || !selectedFYId || !accs?.length) return {};
    const subRef = collection(db, 'companies', activeCompany.id, 'subTransactions');
    const branchId = (activeBranch && activeBranch.id !== 'all') ? activeBranch.id : null;
    const base = [where('financialYearId', '==', selectedFYId)];
    if (branchId) base.push(where('branchId', '==', branchId));

    const byId = {};
    accs.forEach(a => { byId[a.id] = { opening: 0, debit: 0, credit: 0 }; });

    // ── Posted rows: two sums per account (before dateFrom, then the range) ──
    const sumFor = async (accountId, bounds) => {
      const snap = await getAggregateFromServer(
        query(subRef, ...base, where('status', '==', 'Posted'),
          where('accountId', '==', accountId), ...bounds),
        { dr: sum('debitAmount'), cr: sum('creditAmount') }
      );
      recordReads(activeCompany.id, 1);
      const d = snap.data();
      return { dr: Number(d.dr) || 0, cr: Number(d.cr) || 0 };
    };

    await Promise.all(accs.map(async (a) => {
      const b = byId[a.id];
      try {
        if (dateFrom) {
          const o = await sumFor(a.id, [where('docDate', '<', dateFrom)]);
          b.opening += o.dr - o.cr;                       // asset: debit-positive
        }
        const periodBounds = [];
        if (dateFrom) periodBounds.push(where('docDate', '>=', dateFrom));
        if (dateTo) periodBounds.push(where('docDate', '<=', dateTo));
        const p = await sumFor(a.id, periodBounds);
        b.debit += p.dr;
        b.credit += p.cr;
      } catch (e) {
        console.error('Balance aggregation failed for account', a.id, e);
      }
    }));

    // ── Draft rows that are Opening Balance vouchers (few) ──
    try {
      const obSnap = await getDocs(query(
        subRef, ...base,
        where('status', '==', 'Draft'),
        where('tranTypeCode', '==', 'OB')
      ));
      recordReads(activeCompany.id, obSnap.size);
      obSnap.docs.forEach(doc => {
        const t = doc.data();
        const b = byId[t.accountId];
        if (!b) return;
        const d = t.docDate || '';
        const dr = Number(t.debitAmount) || 0;
        const cr = Number(t.creditAmount) || 0;
        if (dateFrom && d < dateFrom) b.opening += dr - cr;
        else if ((!dateFrom || d >= dateFrom) && (!dateTo || d <= dateTo)) { b.debit += dr; b.credit += cr; }
      });
    } catch (e) {
      console.error('Opening-balance rows failed:', e);
    }

    return byId;
  }, [activeCompany, selectedFYId, activeBranch, dateFrom, dateTo]);

  useEffect(() => { loadFinancialYears(); }, [loadFinancialYears]);

  useEffect(() => {
    const fy = financialYears.find(f => f.id === selectedFYId);
    if (fy) setDateFrom(fy.startDate || '');
  }, [selectedFYId, financialYears]);

  const handleGenerateReport = async () => {
    if (!activeCompany?.id || !selectedFYId) return;
    setLoading(true);
    setHasGenerated(true);
    try {
      const accs = await loadAccounts();
      setAccounts(accs);
      setBalances(await loadBalances(accs));
    } catch (error) {
      console.error('Error generating report:', error);
    } finally {
      setLoading(false);
    }
  };

  // Dates are part of the server-side sums now, so changing them must
  // recalculate. Cheap (a couple of small queries per account) but no longer
  // free — it only runs once the report has been generated.
  useEffect(() => {
    if (!hasGenerated || accounts.length === 0) return;
    let cancelled = false;
    setLoading(true);
    loadBalances(accounts)
      .then(b => { if (!cancelled) setBalances(b); })
      .catch(e => console.error('Error recalculating balances:', e))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo]);

  // Balance per cash/bank account: opening (before dateFrom) + period Dr/Cr (within range).
  const reportRows = useMemo(() => {
    return accounts.map(a => {
      const b = balances[a.id] || { opening: 0, debit: 0, credit: 0 };
      const closing = b.opening + b.debit - b.credit;
      return { id: a.id, code: a.code, name: a.name, kind: acctKind(a.code), opening: b.opening, debit: b.debit, credit: b.credit, closing };
    });
  }, [accounts, balances]);

  const filteredRows = useMemo(() => {
    let rows = [...reportRows];
    if (kindFilter !== 'all') rows = rows.filter(r => r.kind === kindFilter);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      rows = rows.filter(r => (r.name || '').toLowerCase().includes(q) || (r.code || '').toLowerCase().includes(q));
    }
    rows.sort((a, b) => {
      let valA, valB;
      switch (sortField) {
        case 'name': valA = (a.name || '').toLowerCase(); valB = (b.name || '').toLowerCase(); break;
        case 'kind': valA = a.kind; valB = b.kind; break;
        case 'opening': valA = a.opening; valB = b.opening; break;
        case 'debit': valA = a.debit; valB = b.debit; break;
        case 'credit': valA = a.credit; valB = b.credit; break;
        case 'closing': valA = a.closing; valB = b.closing; break;
        default: valA = a.code || ''; valB = b.code || '';
      }
      if (valA < valB) return sortDir === 'asc' ? -1 : 1;
      if (valA > valB) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return rows;
  }, [reportRows, kindFilter, searchQuery, sortField, sortDir]);

  const summary = useMemo(() => {
    let cash = 0, bank = 0, debit = 0, credit = 0;
    filteredRows.forEach(r => {
      if (r.kind === 'Cash') cash += r.closing;
      else if (r.kind === 'Bank') bank += r.closing;
      debit += r.debit; credit += r.credit;
    });
    return { cash, bank, total: cash + bank, debit, credit };
  }, [filteredRows]);

  const handleSort = (field) => {
    if (sortField === field) setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };
  const SortIcon = ({ field }) => (sortField !== field ? null : (sortDir === 'asc' ? <FiArrowUp size={12} /> : <FiArrowDown size={12} />));

  const handleRowClick = (row) => {
    navigate('/reports/ledger', { state: { filterAccountId: row.id, filterAccountName: row.name } });
  };

  const handleExport = () => {
    if (filteredRows.length === 0) return;
    const selectedFY = financialYears.find(f => f.id === selectedFYId);
    let csv = 'Cash & Bank Balances Report\n';
    csv += `Company: ${activeCompany?.companyName || ''}\n`;
    csv += `Financial Year: ${selectedFY?.yearName || selectedFY?.yearCode || ''}\n`;
    csv += `Period: ${formatDate(dateFrom)} to ${formatDate(dateTo)}\n\n`;
    csv += '#,Code,Account,Type,Opening,Debit,Credit,Closing\n';
    filteredRows.forEach((row, i) => {
      csv += `${i + 1},"${row.code}","${(row.name || '').replace(/"/g, '""')}","${row.kind}",`;
      csv += `${row.opening.toFixed(2)},${row.debit.toFixed(2)},${row.credit.toFixed(2)},${row.closing.toFixed(2)}\n`;
    });
    csv += `\n,,,,${filteredRows.reduce((s, r) => s + r.opening, 0).toFixed(2)},${summary.debit.toFixed(2)},${summary.credit.toFixed(2)},${summary.total.toFixed(2)}\n`;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `Cash_Bank_Balances_${dateFrom}_to_${dateTo}.csv`; a.click();
    window.URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    const selectedFY = financialYears.find(f => f.id === selectedFYId);
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Cash & Bank Balances</title>
      <style>
        body{font-family:'Segoe UI',sans-serif;margin:30px;color:#1A1A1A;font-size:13px}
        h1{color:#800020;font-size:22px;margin-bottom:5px}
        .meta{color:#666;font-size:12px;margin-bottom:20px}
        .summary{display:flex;gap:30px;margin-bottom:20px;padding:15px;background:#f9f9f9;border-radius:8px}
        .summary-item{text-align:center}
        .summary-label{font-size:11px;color:#666;margin-bottom:4px}
        .summary-value{font-size:16px;font-weight:600}
        table{width:100%;border-collapse:collapse;margin-bottom:15px}
        th{background:#800020;color:white;padding:8px 12px;text-align:left;font-size:11px;text-transform:uppercase}
        th.amount{text-align:right}
        td{padding:6px 12px;border-bottom:1px solid #ddd;font-size:12px}
        td.amount{text-align:right;font-family:'Consolas',monospace}
        .total-row{background:#800020;color:white;font-weight:700}
        .total-row td{border-bottom:none;padding:10px 12px}
        .positive{color:#1565C0} .negative{color:#C62828}
        @media print{body{margin:15px}}
      </style></head><body>
      <h1>Cash & Bank Balances</h1>
      <div class="meta">${activeCompany?.companyName || 'Company'} | Financial Year: ${selectedFY?.yearName || selectedFY?.yearCode || ''} | Period: ${formatDate(dateFrom)} to ${formatDate(dateTo)}</div>
      <div class="summary">
        <div class="summary-item"><div class="summary-label">Total Cash</div><div class="summary-value">${formatAmount(summary.cash)}</div></div>
        <div class="summary-item"><div class="summary-label">Total Bank</div><div class="summary-value">${formatAmount(summary.bank)}</div></div>
        <div class="summary-item"><div class="summary-label">Grand Total</div><div class="summary-value">${formatAmount(summary.total)}</div></div>
      </div>
      <table>
        <thead><tr><th>#</th><th>Code</th><th>Account</th><th>Type</th><th class="amount">Opening</th><th class="amount">Debit</th><th class="amount">Credit</th><th class="amount">Closing</th></tr></thead>
        <tbody>
          ${filteredRows.map((row, i) => `<tr>
            <td>${i + 1}</td><td>${row.code}</td><td>${row.name}</td><td>${row.kind}</td>
            <td class="amount">${formatAmount(row.opening)}</td>
            <td class="amount">${formatAmount(row.debit)}</td>
            <td class="amount">${formatAmount(row.credit)}</td>
            <td class="amount ${row.closing >= 0 ? 'positive' : 'negative'}">${formatAmount(row.closing)}</td>
          </tr>`).join('')}
          <tr class="total-row">
            <td colspan="4" style="text-align:right">Grand Total</td>
            <td class="amount">${formatAmount(filteredRows.reduce((s, r) => s + r.opening, 0))}</td>
            <td class="amount">${formatAmount(summary.debit)}</td>
            <td class="amount">${formatAmount(summary.credit)}</td>
            <td class="amount">${formatAmount(summary.total)}</td>
          </tr>
        </tbody>
      </table>
    </body></html>`);
    addPrintBrand(w);
    w.document.close();
    w.print();
  };

  return (
    <PageLayout pageTitle="Cash & Bank Balances" breadcrumb="Reports / Accounting Reports / Cash & Bank Balances">
      <FiltersCard>
        <FiltersRow>
          <FilterGroup>
            <Label>Financial Year</Label>
            <Select value={selectedFYId} onChange={e => setSelectedFYId(e.target.value)}>
              <option value="">Select Financial Year</option>
              {financialYears.map(fy => (
                <option key={fy.id} value={fy.id}>{fy.yearName || fy.yearCode}{fy.isActive ? ' (Active)' : ''}</option>
              ))}
            </Select>
          </FilterGroup>
          <FilterGroup>
            <Label>Date From</Label>
            <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
          </FilterGroup>
          <FilterGroup>
            <Label>Date To</Label>
            <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} />
          </FilterGroup>
          <FilterGroup>
            <Label>Type</Label>
            <Select value={kindFilter} onChange={e => setKindFilter(e.target.value)}>
              <option value="all">Cash & Bank</option>
              <option value="Cash">Cash only</option>
              <option value="Bank">Bank only</option>
            </Select>
          </FilterGroup>
          <SearchWrap>
            <SearchIcon><FiSearch /></SearchIcon>
            <SearchInput type="text" placeholder="Search account name / code..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
          </SearchWrap>
          <ButtonGroup>
            <Button onClick={handleExport} disabled={filteredRows.length === 0}><FiDownload /> CSV</Button>
            <Button onClick={handlePrint} disabled={filteredRows.length === 0}><FiPrinter /> Print</Button>
            <Button primary onClick={handleGenerateReport} disabled={loading || !selectedFYId}>
              <FiBarChart2 /> {loading ? 'Loading...' : 'Generate'}
            </Button>
          </ButtonGroup>
        </FiltersRow>
      </FiltersCard>

      {hasGenerated && !loading && (
        <SummaryCards>
          <SummaryCard>
            <SummaryIcon color="#0EA5E9"><FiDollarSign /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Total Cash</SummaryLabel>
              <SummaryValue style={{ color: '#0EA5E9' }}>{formatCurrency(summary.cash)}</SummaryValue>
            </SummaryInfo>
          </SummaryCard>
          <SummaryCard>
            <SummaryIcon color="#10B981"><FiCreditCard /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Total Bank</SummaryLabel>
              <SummaryValue style={{ color: '#10B981' }}>{formatCurrency(summary.bank)}</SummaryValue>
            </SummaryInfo>
          </SummaryCard>
          <SummaryCard>
            <SummaryIcon color={colors.primary.main}><FiBarChart2 /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Grand Total (Cash + Bank)</SummaryLabel>
              <SummaryValue>{formatCurrency(summary.total)}</SummaryValue>
            </SummaryInfo>
          </SummaryCard>
          <SummaryCard>
            <SummaryIcon color={colors.text.secondary}>
              <span style={{ fontSize: 16, fontWeight: 700 }}>#</span>
            </SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Accounts</SummaryLabel>
              <SummaryValue>{formatNumber(filteredRows.length)}</SummaryValue>
            </SummaryInfo>
          </SummaryCard>
        </SummaryCards>
      )}

      <ContentCard>
        <TableContainer>
          {loading ? (
            <EmptyState><EmptyIcon><FiBarChart2 /></EmptyIcon><EmptyText>Loading cash & bank balances...</EmptyText></EmptyState>
          ) : !hasGenerated ? (
            <EmptyState><EmptyIcon><FiDollarSign /></EmptyIcon><EmptyText>Select a financial year and click Generate to view cash & bank balances</EmptyText></EmptyState>
          ) : filteredRows.length === 0 ? (
            <EmptyState><EmptyIcon><FiDollarSign /></EmptyIcon><EmptyText>No cash or bank accounts found for the selected criteria</EmptyText></EmptyState>
          ) : (
            <Table>
              <Thead>
                <Tr>
                  <Th style={{ width: 50 }}>#</Th>
                  <Th onClick={() => handleSort('code')} style={{ cursor: 'pointer', width: 110 }}>Code <SortIcon field="code" /></Th>
                  <Th onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>Account <SortIcon field="name" /></Th>
                  <Th onClick={() => handleSort('kind')} style={{ cursor: 'pointer', width: 90 }}>Type <SortIcon field="kind" /></Th>
                  <Th align="right" onClick={() => handleSort('opening')} style={{ cursor: 'pointer', width: 140 }}>Opening <SortIcon field="opening" /></Th>
                  <Th align="right" onClick={() => handleSort('debit')} style={{ cursor: 'pointer', width: 140 }}>Debit <SortIcon field="debit" /></Th>
                  <Th align="right" onClick={() => handleSort('credit')} style={{ cursor: 'pointer', width: 140 }}>Credit <SortIcon field="credit" /></Th>
                  <Th align="right" onClick={() => handleSort('closing')} style={{ cursor: 'pointer', width: 150 }}>Closing <SortIcon field="closing" /></Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredRows.map((row, index) => (
                  <Tr key={row.id}>
                    <Td>{index + 1}</Td>
                    <Td>{row.code}</Td>
                    <Td><ClickableName onClick={() => handleRowClick(row)}>{row.name}</ClickableName></Td>
                    <Td>{row.kind}</Td>
                    <AmountCell align="right">{row.opening !== 0 ? formatAmount(row.opening) : '-'}</AmountCell>
                    <AmountCell align="right">{row.debit > 0 ? formatAmount(row.debit) : '-'}</AmountCell>
                    <AmountCell align="right">{row.credit > 0 ? formatAmount(row.credit) : '-'}</AmountCell>
                    <AmountCell align="right" $isClosing $positive={row.closing >= 0}>{formatAmount(row.closing)}</AmountCell>
                  </Tr>
                ))}
              </Tbody>
              <Tfoot>
                <TotalRow>
                  <Td colSpan={4} style={{ textAlign: 'right', fontWeight: 700, fontSize: 14 }}>Grand Total</Td>
                  <AmountCell align="right" $isTotal>{formatAmount(filteredRows.reduce((s, r) => s + r.opening, 0))}</AmountCell>
                  <AmountCell align="right" $isTotal>{formatAmount(summary.debit)}</AmountCell>
                  <AmountCell align="right" $isTotal>{formatAmount(summary.credit)}</AmountCell>
                  <AmountCell align="right" $isTotal>{formatAmount(summary.total)}</AmountCell>
                </TotalRow>
              </Tfoot>
            </Table>
          )}
        </TableContainer>
      </ContentCard>
    </PageLayout>
  );
};

// Styled Components
const FiltersCard = styled.div`background: white; border-radius: ${colors.borderRadius.lg}; box-shadow: ${colors.shadows.sm}; padding: 20px; margin-bottom: 20px;`;
const FiltersRow = styled.div`display: flex; flex-wrap: wrap; gap: 14px; align-items: flex-end;`;
const FilterGroup = styled.div`display: flex; flex-direction: column; gap: 6px; min-width: 150px;`;
const Label = styled.label`font-size: 11px; font-weight: 600; color: ${colors.text.secondary}; text-transform: uppercase; letter-spacing: 0.3px;`;
const Select = styled.select`padding: 9px 12px; border: 1px solid ${colors.neutral[300]}; border-radius: ${colors.borderRadius.md}; font-size: 13px; color: ${colors.text.primary}; background: white; cursor: pointer; &:focus { outline: none; border-color: ${colors.primary.main}; box-shadow: 0 0 0 3px ${colors.primary.main}15; }`;
const Input = styled.input`padding: 9px 12px; border: 1px solid ${colors.neutral[300]}; border-radius: ${colors.borderRadius.md}; font-size: 13px; color: ${colors.text.primary}; &:focus { outline: none; border-color: ${colors.primary.main}; box-shadow: 0 0 0 3px ${colors.primary.main}15; }`;
const SearchWrap = styled.div`position: relative; flex: 1; min-width: 220px;`;
const SearchIcon = styled.div`position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: ${colors.text.secondary};`;
const SearchInput = styled.input`width: 100%; padding: 9px 12px 9px 38px; border: 1px solid ${colors.neutral[300]}; border-radius: ${colors.borderRadius.md}; font-size: 13px; color: ${colors.text.primary}; &:focus { outline: none; border-color: ${colors.primary.main}; box-shadow: 0 0 0 3px ${colors.primary.main}15; } &::placeholder { color: ${colors.text.tertiary}; }`;
const ButtonGroup = styled.div`display: flex; gap: 8px;`;
const Button = styled.button`padding: 9px 14px; border: 1px solid ${colors.neutral[300]}; border-radius: ${colors.borderRadius.md}; font-size: 13px; font-weight: 500; cursor: pointer; display: flex; align-items: center; gap: 6px; white-space: nowrap; ${props => props.primary ? `background: ${colors.primary.main}; color: white; border-color: ${colors.primary.main}; &:hover:not(:disabled) { background: ${colors.primary.dark}; }` : `background: white; color: ${colors.text.primary}; &:hover:not(:disabled) { background: ${colors.neutral[100]}; border-color: ${colors.primary.main}; }`} &:disabled { opacity: 0.5; cursor: not-allowed; }`;
const SummaryCards = styled.div`display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 16px; margin-bottom: 20px;`;
const SummaryCard = styled.div`background: white; border-radius: ${colors.borderRadius.lg}; box-shadow: ${colors.shadows.sm}; padding: 20px; display: flex; align-items: center; gap: 16px;`;
const SummaryIcon = styled.div`width: 48px; height: 48px; border-radius: ${colors.borderRadius.md}; background: ${props => (props.color || colors.primary.main) + '15'}; color: ${props => props.color || colors.primary.main}; display: flex; align-items: center; justify-content: center; font-size: 20px; flex-shrink: 0;`;
const SummaryInfo = styled.div`min-width: 0;`;
const SummaryLabel = styled.div`font-size: 12px; color: ${colors.text.secondary}; margin-bottom: 4px;`;
const SummaryValue = styled.div`font-size: 20px; font-weight: 600; color: ${colors.text.primary}; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;`;
const ContentCard = styled.div`background: white; border-radius: ${colors.borderRadius.lg}; box-shadow: ${colors.shadows.sm}; overflow: hidden;`;
const TableContainer = styled.div`overflow-x: auto;`;
const Table = styled.table`width: 100%; border-collapse: collapse;`;
const Thead = styled.thead`background: ${colors.neutral[50]};`;
const Th = styled.th`padding: 12px 14px; text-align: ${props => props.align || 'left'}; font-size: 11px; font-weight: 600; color: ${colors.text.secondary}; text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 2px solid ${colors.neutral[200]}; white-space: nowrap; user-select: none; &:hover { color: ${colors.primary.main}; }`;
const Tbody = styled.tbody``;
const Tfoot = styled.tfoot``;
const Tr = styled.tr`border-bottom: 1px solid ${colors.neutral[100]}; transition: background 0.15s; &:hover { background: ${colors.neutral[50]}; }`;
const Td = styled.td`padding: 10px 14px; font-size: 13px; color: ${colors.text.primary}; text-align: ${props => props.align || 'left'};`;
const AmountCell = styled(Td)`font-family: 'SF Mono', 'Consolas', 'Courier New', monospace; font-size: 13px; text-align: right; white-space: nowrap; ${props => props.$isTotal && `font-weight: 700; font-size: 14px; color: ${colors.text.primary};`} ${props => props.$isClosing && `font-weight: 600; color: ${props.$positive ? '#1565C0' : '#C62828'};`}`;
const ClickableName = styled.span`cursor: pointer; color: #1a73e8; &:hover { text-decoration: underline; }`;
const TotalRow = styled.tr`background: ${colors.neutral[100]}; border-top: 2px solid ${colors.primary.main}; td { padding: 14px; }`;
const EmptyState = styled.div`text-align: center; padding: 60px 20px; color: ${colors.text.secondary};`;
const EmptyIcon = styled.div`font-size: 48px; color: ${colors.neutral[300]}; margin-bottom: 16px;`;
const EmptyText = styled.p`font-size: 16px; margin-bottom: 8px;`;

export default CashBankBalances;
